import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Upload, Download, Filter, Edit3, ExternalLink } from "lucide-react";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Input, Button, Select } from "@/components/controls";
import { usePerms } from "@/lib/auth";
import PaginationBar from "@/components/PaginationBar";
import FieldFilterBar, { applyFieldFilters } from "@/components/FieldFilterBar";
import FilterPresets from "@/components/FilterPresets";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import ImageThumb from "@/components/ImageThumb";

// Mar 1 2026 — every visible column is now a filterable field. Each supports
// multi-select + a "(Blank)" option (built into FieldFilterBar as `select`
// type — the empty-string sentinel matches null/empty/whitespace values).
const FILTER_FIELDS = [
  { key: "account_id", label: "Account ID", type: "select" },
  { key: "warehouse_name", label: "Warehouse", type: "select" },
  { key: "seller_sku", label: "Seller-SKU", type: "text" },
  { key: "seller_asin", label: "Seller-ASIN", type: "text" },
  { key: "system_sku", label: "System-SKU", type: "text" },
  { key: "seller_item_name", label: "Item Name", type: "text" },
  { key: "seller_status", label: "Status", type: "select" },
  { key: "seller_tax_code", label: "Tax Code", type: "select" },
  { key: "seller_price", label: "Seller Price", type: "money-range" },
  { key: "seller_mrp", label: "Seller MRP", type: "money-range" },
  { key: "seller_qty", label: "Seller Qty", type: "money-range" },
  { key: "stock_qty", label: "Stock Qty", type: "money-range" },
  { key: "listing_id", label: "Listing ID", type: "text" },
  { key: "flipkart_stock_quantity", label: "Flipkart Stock", type: "money-range" },
];

const money = (v) =>
  v === null || v === undefined || v === "" ? "" :
    "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Catalog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [advFilters, setAdvFilters] = useState({});
  const [importFile, setImportFile] = useState(null);
  const [importAccount, setImportAccount] = useState("");
  // Mar 1 2026 — Import Type selector inside the modal.
  const [importType, setImportType] = useState("amazon");   // "amazon" | "flipkart"
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJob, setImportJob] = useState(null);
  // Mar 1 2026 — top-bar Warehouse filter + Wide/Compact density toggle
  // (same patterns as the Orders module).
  const [warehouseFilter, setWarehouseFilter] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  // Sep 15 2026 — bulk-delete state (mirrors ModulePage pattern used by
  // Mapping): selectedIds + confirm modal + in-flight guard. Header/row
  // checkboxes toggle these; the toolbar Delete button opens the confirm.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Sep 20 2026 — Delete-by-File flow (upload the export CSV, preview
  // matches, confirm to delete). Mirrors the pattern of the standalone
  // Import modal so ops muscle-memory carries over.
  const [showDeleteByFileModal, setShowDeleteByFileModal] = useState(false);
  const [deleteFile, setDeleteFile] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null); // {matched, missing, sample_matched, sample_missing, rows_parsed}
  const [deleteByFileBusy, setDeleteByFileBusy] = useState(false);
  const [density, setDensity] = useState(() => localStorage.getItem("catalog_density") || "compact");
  const setDensityPersist = (d) => { setDensity(d); localStorage.setItem("catalog_density", d); };

  const { isAdmin, allowedWarehouses } = usePerms();

  // Mar 1 2026 — the account list narrows to the currently-selected import
  // type, so the ID dropdown never shows an Amazon ID under a Flipkart flow.
  const marketplaceFor = (t) => (t === "flipkart" ? "flipkart" : "amazon");
  const importAccountOptions = useMemo(() => {
    const target = marketplaceFor(importType);
    return (accounts || []).filter((a) =>
      (a.marketplace || "").toLowerCase() === target
    );
  }, [accounts, importType]);
  // Locked warehouse is fully derived from the selected account_id.
  const lockedWarehouse = useMemo(() => {
    const a = (accounts || []).find((x) => x.account_id === importAccount);
    return (a && a.warehouse_name) || "";
  }, [accounts, importAccount]);

  // Aug 5 2026 — the backend wipe endpoint existed but was never wired to a
  // button, so the feature was unreachable. Mirrors the Inventory / Mapping /
  // Orders pattern: typed confirmation phrase, admin-only.
  const doWipe = async () => {
    const phrase = "WIPE ALL CATALOG";
    const entry = window.prompt(
      `This will permanently delete EVERY catalog row.\nType "${phrase}" to confirm.`,
    );
    if (entry !== phrase) {
      if (entry !== null) toast.error("Confirmation phrase did not match — wipe cancelled.");
      return;
    }
    try {
      const { data } = await api.post("/catalog/admin/wipe", { confirm: phrase });
      toast.success(`Wiped ${data.deleted} catalog rows.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = async (searchQuery, pg) => {
    setBusy(true);
    try {
      const params = { page: pg || page, page_size: pageSize };
      const s = searchQuery !== undefined ? searchQuery : q;
      if (s) params.search = s;
      if (accountId) params.account_id = accountId;
      // Mar 1 2026 — top-bar warehouse filter is server-side.
      if (warehouseFilter.length) params.warehouse_name = warehouseFilter.join(",");
      const { data } = await api.get("/catalog", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const loadAccounts = async () => {
    try {
      const { data } = await api.get("/accounts");
      setAccounts(data || []);
    } catch { /* silent */ }
  };
  const loadWarehouses = async () => {
    try {
      const { data } = await api.get("/warehouse");
      const items = Array.isArray(data) ? data : (data?.items || []);
      setWarehouses(items);
    } catch { /* silent */ }
  };

  useEffect(() => { loadAccounts(); loadWarehouses(); load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, accountId, warehouseFilter]);

  const filtered = useMemo(
    () => rows.filter((r) => applyFieldFilters(r, advFilters, FILTER_FIELDS)),
    [rows, advFilters]
  );
  const activeAdvCount = Object.keys(advFilters).filter((k) => {
    const v = advFilters[k];
    if (!v) return false;
    if (typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== undefined);
    return true;
  }).length;

  // Sep 15 2026 — bulk-delete helpers (mirrors ModulePage).
  const allSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
  const someSelected = !allSelected && filtered.some((r) => selectedIds.has(r.id));
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach((r) => next.delete(r.id));
      else filtered.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const runBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      let deletedCount = 0;
      try {
        const { data } = await api.post("/catalog/bulk-delete", { ids });
        deletedCount = data?.deleted ?? ids.length;
      } catch (err) {
        // Legacy fallback (mirrors ModulePage) — per-id DELETE if the batch
        // endpoint isn't shipped yet.
        if (err?.response?.status === 404) {
          for (const id of ids) { await api.delete(`/catalog/${id}`); }
          deletedCount = ids.length;
        } else { throw err; }
      }
      setRows((rs) => rs.filter((r) => !selectedIds.has(r.id)));
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      toast.success(`${deletedCount} row${deletedCount === 1 ? "" : "s"} deleted.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkDeleting(false); }
  };

  // Sep 20 2026 — Delete-by-File preview: upload the sheet and ask the
  // server how many catalog rows would be removed, without touching data.
  const previewDeleteByFile = async () => {
    if (!deleteFile) { toast.error("Choose a file first"); return; }
    setDeleteByFileBusy(true);
    setDeletePreview(null);
    try {
      const fd = new FormData();
      fd.append("file", deleteFile);
      const { data } = await api.post(
        "/catalog/bulk-delete-by-file?dry_run=true",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDeletePreview(data);
      if ((data?.matched ?? 0) === 0) {
        toast.error("No matching catalog rows found in the uploaded file.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteByFileBusy(false);
    }
  };

  // Sep 20 2026 — Delete-by-File confirm: caller only reaches here after
  // seeing `deletePreview.matched`, so no extra guard needed here.
  const confirmDeleteByFile = async () => {
    if (!deleteFile) return;
    setDeleteByFileBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", deleteFile);
      const { data } = await api.post(
        "/catalog/bulk-delete-by-file?dry_run=false",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${data.deleted ?? 0} catalog rows deleted (${data.missing ?? 0} unmatched).`);
      setShowDeleteByFileModal(false);
      setDeleteFile(null);
      setDeletePreview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteByFileBusy(false);
    }
  };

  const save = async () => {
    try {
      if (creating) await api.post("/catalog", creating);
      else await api.put(`/catalog/${editing.id}`, editing);
      toast.success("Saved");
      setCreating(null); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this catalog row?")) return;
    try { await api.delete(`/catalog/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const dlTemplate = async () => {
    try { await downloadFile("/catalog/template", "catalog_template.csv"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const dlExport = async () => {
    try {
      const url = accountId ? `/catalog/export?account_id=${encodeURIComponent(accountId)}` : "/catalog/export";
      await downloadFile(url, "catalog_export.csv");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitImport = async () => {
    if (!importAccount) { toast.error("Select an Account ID first"); return; }
    if (!importFile) { toast.error("Choose a CSV file"); return; }
    const fd = new FormData();
    fd.append("file", importFile);
    // Mar 1 2026 — route based on the import type. Amazon = existing catalog
    // import; Flipkart = new stock-import endpoint that only touches
    // listing_id / seller_asin / flipkart_stock_quantity by composite key.
    const url = importType === "flipkart"
      ? `/catalog/import/flipkart?account_id=${encodeURIComponent(importAccount)}`
      : `/catalog/import?account_id=${encodeURIComponent(importAccount)}`;
    try {
      const { data } = await api.post(url, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Import queued — ${data.total} rows. Polling for progress...`);
      setImportJob({ job_id: data.job_id, total: data.total, processed: 0, status: "running" });
      // Poll status every 1.5s until done.
      const pollId = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/imports/status/${data.job_id}`);
          setImportJob(s);
          if (s.status !== "running") {
            clearInterval(pollId);
            if (s.status === "failed") {
              toast.error(`Import failed: ${s.error || "unknown"}`);
            } else {
              const r = s.result || {};
              toast.success(`Import done — created:${r.created ?? 0} updated:${r.updated ?? 0} skipped:${r.skipped ?? 0}`);
              load();
            }
          }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Mar 1 2026 — Amazon .xlsx + Flipkart .csv exports (top bar).
  // Mar 2 2026 (v3) — Amazon export now returns a ZIP bundle with one
  // standalone .xlsx per Amazon account. Server sends the .zip filename via
  // Content-Disposition; the fallback below mirrors it for safety.
  const exportAmazonXlsm = async () => {
    try {
      const wh = warehouseFilter.length ? `?warehouse_name=${encodeURIComponent(warehouseFilter.join(","))}` : "";
      await downloadFile(`/catalog/export/amazon-xlsm${wh}`, "catalog_amazon_export.zip");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const exportFlipkartCsv = async () => {
    try {
      const wh = warehouseFilter.length ? `?warehouse_name=${encodeURIComponent(warehouseFilter.join(","))}` : "";
      await downloadFile(`/catalog/export/flipkart-csv${wh}`, "catalog_flipkart_export.csv");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader title="Catalog" subtitle={`${total.toLocaleString()} rows · Amazon per-account seller catalog`}
                   testId="catalog-page-header" />
      <PageBody>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Input testId="catalog-search" placeholder="Search SKU / ASIN / name..." value={q}
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && (setPage(1), load(q, 1))}
                 className="w-64" />
          <Select value={accountId}
                  onChange={(v) => { setAccountId(v); setPage(1); }}
                  options={["", ...accounts.map((a) => a.account_id)]}
                  allowEmpty={false}
                  testId="catalog-account-filter"
                  className="w-56" />
          <Button testId="catalog-filter-toggle" onClick={() => setShowFilterBar((s) => !s)}
                  className={showFilterBar ? "!bg-blue-100 !border-blue-300" : ""}>
            <Filter size={13} /> Filter{activeAdvCount ? ` (${activeAdvCount})` : ""}
          </Button>
          <FilterPresets moduleKey="catalog" activeFilters={advFilters}
                          onApply={(f) => setAdvFilters(f || {})} />
          <div className="ml-auto flex items-center gap-2">
            <Button testId="catalog-template" onClick={dlTemplate}
                    className="!bg-slate-100 hover:!bg-slate-200">
              <Download size={13} /> Template
            </Button>
            <Button testId="catalog-import" onClick={() => setShowImportModal(true)}
                    className="!bg-blue-600 hover:!bg-blue-700 !text-white !border-blue-600">
              <Upload size={13} /> Import
            </Button>
            {/* Sep 20 2026 — Delete-by-File: upload the export sheet and
                remove all matched rows after a confirm dialog. */}
            <Button testId="catalog-delete-by-file" onClick={() => {
              setShowDeleteByFileModal(true);
              setDeleteFile(null);
              setDeletePreview(null);
            }}
                    className="!bg-rose-600 hover:!bg-rose-700 !text-white !border-rose-600">
              <Trash2 size={13} /> Delete by File
            </Button>
            <Button testId="catalog-export" onClick={dlExport}
                    className="!bg-emerald-600 hover:!bg-emerald-700 !text-white !border-emerald-600">
              <Download size={13} /> Export CSV
            </Button>
            {/* Mar 1 2026 — new export buttons (Amazon .xlsm + Flipkart .csv). */}
            <Button testId="catalog-export-amazon-xlsm" onClick={exportAmazonXlsm}
                    className="!bg-amber-600 hover:!bg-amber-700 !text-white !border-amber-600">
              <Download size={13} /> Amazon Export
            </Button>
            <Button testId="catalog-export-flipkart-csv" onClick={exportFlipkartCsv}
                    className="!bg-sky-600 hover:!bg-sky-700 !text-white !border-sky-600">
              <Download size={13} /> Flipkart Export
            </Button>
            {/* Mar 1 2026 — Warehouse filter (top bar, same pattern as Orders). */}
            <MultiSelectFilter
              label="Warehouse"
              options={(() => {
                const all = warehouses.map((w) => w.name).filter(Boolean);
                const allow = allowedWarehouses ? allowedWarehouses() : null;
                return allow ? all.filter((n) => allow.includes(n)) : all;
              })()}
              value={warehouseFilter}
              onChange={(next) => { setWarehouseFilter(next); setPage(1); }}
              testId="catalog-filter-warehouse"
            />
            {/* Mar 1 2026 — Wide/Compact density toggle. */}
            <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden h-8" data-testid="catalog-density-toggle">
              <button onClick={() => setDensityPersist("compact")}
                      data-testid="catalog-density-compact"
                      className={`h-8 px-3 text-[12px] font-semibold ${density === "compact" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>Compact</button>
              <button onClick={() => setDensityPersist("wide")}
                      data-testid="catalog-density-wide"
                      className={`h-8 px-3 text-[12px] font-semibold border-l border-slate-200 ${density === "wide" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>Wide</button>
            </div>
            {selectedIds.size > 0 && (
              <Button
                testId="catalog-bulk-delete-btn"
                onClick={() => setBulkDeleteConfirm(true)}
                className="!bg-red-600 hover:!bg-red-700 !text-white !border-red-600"
              >
                <Trash2 size={13} /> Delete {selectedIds.size} Selected
              </Button>
            )}
            <Button testId="catalog-new" onClick={() => setCreating({
              account_id: accountId || "", seller_sku: "", seller_asin: "",
              seller_item_name: "", seller_price: "", seller_qty: "",
              seller_tax_code: "", seller_status: "", seller_mrp: "",
            })} className="!bg-slate-800 hover:!bg-slate-900 !text-white !border-slate-800">
              <Plus size={13} /> New
            </Button>
            {isAdmin && (
              <Button testId="catalog-wipe" onClick={doWipe}
                      className="!bg-red-600 hover:!bg-red-700 !text-white !border-red-600">
                <Trash2 size={13} /> Wipe All
              </Button>
            )}
          </div>
        </div>

        {showFilterBar && (
          <FieldFilterBar fields={FILTER_FIELDS} filters={advFilters}
                          onChange={setAdvFilters} onClose={() => setShowFilterBar(false)}
                          onClear={() => setAdvFilters({})} />
        )}

        <div className="overflow-auto border border-slate-200 rounded-sm bg-white" data-testid="catalog-table">
          <table className="w-full text-[11.5px]">
            <thead className="bg-slate-100 sticky top-0">
              <tr className="text-left text-slate-700">
                <th className="px-2 py-1.5 font-semibold w-8 text-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleSelectAllVisible}
                    data-testid="catalog-select-all"
                    title="Select all rows on this page"
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th className="px-2 py-1.5 font-semibold w-16">Image</th>
                <th className="px-2 py-1.5 font-semibold">Account ID</th>
                <th className="px-2 py-1.5 font-semibold">Seller-SKU</th>
                <th className="px-2 py-1.5 font-semibold">Seller-ASIN</th>
                <th className="px-2 py-1.5 font-semibold">URL</th>
                <th className="px-2 py-1.5 font-semibold text-right">Seller-Price</th>
                <th className="px-2 py-1.5 font-semibold">System-SKU</th>
                <th className="px-2 py-1.5 font-semibold">Item Name</th>
                <th className="px-2 py-1.5 font-semibold text-right">Seller-Qty</th>
                <th className="px-2 py-1.5 font-semibold">Tax Code</th>
                <th className="px-2 py-1.5 font-semibold">Status</th>
                <th className="px-2 py-1.5 font-semibold text-right">Seller-MRP</th>
                <th className="px-2 py-1.5 font-semibold text-right">Stock</th>
                <th className="px-2 py-1.5 font-semibold w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {busy && filtered.length === 0 && (
                <tr><td colSpan={15} className="text-center py-6 text-slate-400">Loading…</td></tr>
              )}
              {!busy && filtered.length === 0 && (
                <tr><td colSpan={15} className="text-center py-6 text-slate-400">No catalog rows yet. Import an Amazon Listing Report to get started.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50 ${selectedIds.has(r.id) ? "bg-blue-50" : ""}`} data-testid={`catalog-row-${r.seller_sku}`}>
                  <td className="px-2 py-1 text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                      data-testid={`catalog-row-check-${r.id}`}
                    />
                  </td>
                  <td className="px-2 py-1">
                    {r.image_url
                      ? <ImageThumb src={r.image_url} size={64} testId={`catalog-thumb-${r.seller_sku}`} />
                      : <div className="w-16 h-16 bg-slate-100 rounded-sm border border-slate-200 flex items-center justify-center text-[9px] text-slate-400">no-img</div>}
                  </td>
                  <td className="px-2 py-1 font-mono">{r.account_id}</td>
                  <td className="px-2 py-1 font-mono">{r.seller_sku}</td>
                  <td className="px-2 py-1 font-mono">{r.seller_asin}</td>
                  <td className="px-2 py-1">
                    {r.product_url && (
                      <a href={r.product_url} target="_blank" rel="noopener noreferrer"
                         className="text-blue-600 hover:underline inline-flex items-center gap-1">
                        <ExternalLink size={11} /> View
                      </a>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right font-mono">{money(r.seller_price)}</td>
                  <td className="px-2 py-1 font-mono">{r.system_sku || <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-1 max-w-xs truncate" title={r.seller_item_name}>{r.seller_item_name}</td>
                  <td className="px-2 py-1 text-right font-mono">{r.seller_qty ?? ""}</td>
                  <td className="px-2 py-1 font-mono">{r.seller_tax_code}</td>
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase ${
                      (r.seller_status || "").toLowerCase() === "active"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-slate-100 text-slate-600"
                    }`}>{r.seller_status || "—"}</span>
                  </td>
                  <td className="px-2 py-1 text-right font-mono">{money(r.seller_mrp)}</td>
                  <td className={`px-2 py-1 text-right font-mono font-semibold ${
                    r.stock_qty === 0 ? "text-red-700" : r.stock_qty < 5 ? "text-amber-700" : "text-emerald-700"
                  }`}>{r.stock_qty}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => setEditing({ ...r })} data-testid={`catalog-edit-${r.seller_sku}`}
                            className="text-blue-600 hover:text-blue-800 mr-1.5">
                      <Edit3 size={13} />
                    </button>
                    <button onClick={() => del(r.id)} data-testid={`catalog-delete-${r.seller_sku}`}
                            className="text-red-600 hover:text-red-800">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PaginationBar page={page} pageSize={pageSize} total={total}
                        onPageChange={(p) => setPage(p)} testIdPrefix="catalog-pagination" />

        {/* Create / Edit modal */}
        {(creating || editing) && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-sm border border-slate-300 w-full max-w-lg" data-testid="catalog-edit-modal">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                <div className="font-semibold text-sm">{creating ? "New Catalog Row" : "Edit Catalog Row"}</div>
                <button onClick={() => { setCreating(null); setEditing(null); }} className="text-slate-500 hover:text-slate-800">
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <Field label="Account ID *">
                  <Select value={(creating || editing).account_id || ""}
                          disabled={!creating}
                          options={accounts.map((a) => a.account_id)}
                          onChange={(v) => creating && setCreating((s) => ({ ...s, account_id: v }))}
                          testId="catalog-modal-account" />
                </Field>
                <Field label="Seller-SKU *">
                  <Input value={(creating || editing).seller_sku || ""}
                          disabled={!creating}
                          onChange={(e) => creating && setCreating((s) => ({ ...s, seller_sku: e.target.value }))}
                          testId="catalog-modal-seller-sku" />
                </Field>
                <Field label="Seller-ASIN">
                  <Input value={(creating || editing).seller_asin || ""}
                          onChange={(e) => creating
                            ? setCreating((s) => ({ ...s, seller_asin: e.target.value }))
                            : setEditing((s) => ({ ...s, seller_asin: e.target.value }))}
                          testId="catalog-modal-asin" />
                </Field>
                <Field label="Seller Status">
                  <Input value={(creating || editing).seller_status || ""}
                          onChange={(e) => creating
                            ? setCreating((s) => ({ ...s, seller_status: e.target.value }))
                            : setEditing((s) => ({ ...s, seller_status: e.target.value }))} />
                </Field>
                <Field label="Item Name" className="col-span-2">
                  <Input value={(creating || editing).seller_item_name || ""}
                          onChange={(e) => creating
                            ? setCreating((s) => ({ ...s, seller_item_name: e.target.value }))
                            : setEditing((s) => ({ ...s, seller_item_name: e.target.value }))} />
                </Field>
                <Field label="Seller-Price">
                  <Input type="number" step="0.01" value={(creating || editing).seller_price ?? ""}
                          onChange={(e) => creating
                            ? setCreating((s) => ({ ...s, seller_price: e.target.value }))
                            : setEditing((s) => ({ ...s, seller_price: e.target.value }))} />
                </Field>
                <Field label="Seller-MRP">
                  <Input type="number" step="0.01" value={(creating || editing).seller_mrp ?? ""}
                          onChange={(e) => creating
                            ? setCreating((s) => ({ ...s, seller_mrp: e.target.value }))
                            : setEditing((s) => ({ ...s, seller_mrp: e.target.value }))} />
                </Field>
                <Field label="Seller-Qty">
                  <Input type="number" value={(creating || editing).seller_qty ?? ""}
                          onChange={(e) => creating
                            ? setCreating((s) => ({ ...s, seller_qty: e.target.value }))
                            : setEditing((s) => ({ ...s, seller_qty: e.target.value }))} />
                </Field>
                <Field label="Tax Code">
                  <Input value={(creating || editing).seller_tax_code || ""}
                          onChange={(e) => creating
                            ? setCreating((s) => ({ ...s, seller_tax_code: e.target.value }))
                            : setEditing((s) => ({ ...s, seller_tax_code: e.target.value }))} />
                </Field>
              </div>
              <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <Button onClick={() => { setCreating(null); setEditing(null); }}>Cancel</Button>
                <Button onClick={save} testId="catalog-modal-save"
                        className="!bg-emerald-600 hover:!bg-emerald-700 !text-white !border-emerald-600">Save</Button>
              </div>
            </div>
          </div>
        )}

        {/* Import modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-sm border border-slate-300 w-full max-w-md" data-testid="catalog-import-modal">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                <div className="font-semibold text-sm">Import Catalog</div>
                <button onClick={() => { setShowImportModal(false); setImportFile(null); setImportJob(null); }}
                        className="text-slate-500 hover:text-slate-800">
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                {/* Mar 1 2026 — Import Type selector. Amazon = existing 44-col
                    Listing Report flow. Flipkart = new 4-col stock file that
                    upserts by (system_sku, warehouse_name). */}
                <Field label="Import Type *">
                  <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden">
                    <button type="button" data-testid="catalog-import-type-amazon"
                      onClick={() => { setImportType("amazon"); setImportAccount(""); setImportFile(null); }}
                      className={`h-8 px-3 text-[12px] font-semibold ${importType === "amazon" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                    >Amazon</button>
                    <button type="button" data-testid="catalog-import-type-flipkart"
                      onClick={() => { setImportType("flipkart"); setImportAccount(""); setImportFile(null); }}
                      className={`h-8 px-3 text-[12px] font-semibold border-l border-slate-200 ${importType === "flipkart" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                    >Flipkart</button>
                  </div>
                </Field>
                <Field label="Account ID *">
                  <Select value={importAccount} onChange={(v) => setImportAccount(v)}
                          options={importAccountOptions.map((a) => a.account_id)}
                          testId="catalog-import-account" />
                </Field>
                {/* Mar 1 2026 — Warehouse is auto-populated from the selected
                    account and locked. Never user-editable. */}
                <Field label="Warehouse (auto)">
                  <input
                    type="text"
                    value={lockedWarehouse || ""}
                    readOnly
                    disabled
                    placeholder={importAccount ? "Account has no warehouse" : "Pick an account first"}
                    data-testid="catalog-import-warehouse-locked"
                    className="text-[12px] w-full border border-slate-300 rounded-sm px-2 py-1 bg-slate-100 font-mono text-slate-700"
                  />
                </Field>
                <Field label={importType === "flipkart"
                  ? "Flipkart Stock CSV (columns: listing_id, fsn, sku, stock_quantity)"
                  : "Amazon Listing Report CSV (44 columns)"}>
                  <input type="file"
                          accept={importType === "flipkart" ? ".csv" : ".csv,.txt,.xlsx"}
                          onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                          data-testid="catalog-import-file"
                          className="text-[12px] w-full border border-slate-300 rounded-sm px-2 py-1 bg-white" />
                </Field>
                <div className="text-[10.5px] text-slate-500 leading-relaxed">
                  {importType === "flipkart" ? (
                    <>
                      Only 3 fields written: <code>listing_id</code>, <code>seller_asin (from fsn)</code>, <code>flipkart_stock_quantity</code>.
                      Rows are matched by <code>(sku, warehouse_name)</code>. Rows without a matching catalog record are skipped — no new catalog rows are created.
                    </>
                  ) : (
                    <>Only 8 columns are consumed: <code>seller-sku, asin1, item-name, price, quantity, ProductTaxCode, status, maximum-retail-price</code>. All other columns are ignored. Rows are upserted by (Account ID, Seller-SKU).</>
                  )}
                </div>
                {importJob && (
                  <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="catalog-import-progress">
                    <div className="flex justify-between text-[11px] font-mono text-blue-900 mb-1">
                      <span>Status: {importJob.status}</span>
                      <span>{importJob.processed ?? 0} / {importJob.total ?? 0}</span>
                    </div>
                    <div className="h-1.5 bg-blue-100 rounded-sm overflow-hidden">
                      <div className="h-full bg-blue-600 transition-all"
                            style={{ width: `${importJob.total ? ((importJob.processed ?? 0) / importJob.total) * 100 : 0}%` }} />
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[10.5px] font-mono mt-1.5">
                      <div>Created: <span className="font-semibold text-emerald-700">{importJob.created ?? 0}</span></div>
                      <div>Updated: <span className="font-semibold text-blue-700">{importJob.updated ?? 0}</span></div>
                      <div>Skipped: <span className="font-semibold text-amber-700">{importJob.skipped ?? 0}</span></div>
                    </div>
                    {importJob.log && importJob.log.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto bg-white border border-blue-100 rounded-sm p-1.5 font-mono text-[10px] space-y-0.5">
                        {importJob.log.slice(-15).reverse().map((l, i) => (
                          <div key={i} className={l.status === "error" ? "text-red-700" : l.status === "skipped" ? "text-amber-700" : "text-slate-700"}>
                            row {l.row}: {l.msg}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <Button onClick={() => { setShowImportModal(false); setImportFile(null); setImportJob(null); }}>
                  {importJob?.status === "done" ? "Close" : "Cancel"}
                </Button>
                <Button onClick={submitImport} disabled={importJob?.status === "running"}
                        testId="catalog-import-submit"
                        className="!bg-blue-600 hover:!bg-blue-700 !text-white !border-blue-600 disabled:opacity-60">
                  <Upload size={13} /> Upload
                </Button>
              </div>
            </div>
          </div>
        )}
      </PageBody>
      {/* Sep 15 2026 — bulk-delete confirm modal (mirrors ModulePage). */}
      {bulkDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => !bulkDeleting && setBulkDeleteConfirm(false)}
        >
          <div
            className="bg-white rounded-md shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="catalog-bulk-delete-confirm"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 size={16} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-[14px] font-bold text-slate-900">
                  Delete {selectedIds.size} selected rows?
                </h3>
                <p className="text-[12.5px] text-slate-600 mt-1">
                  This cannot be undone. Each row will be removed permanently and an audit log entry will be written.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <Button
                onClick={() => setBulkDeleteConfirm(false)}
                disabled={bulkDeleting}
                testId="catalog-bulk-delete-cancel"
              >Cancel</Button>
              <Button
                onClick={runBulkDelete}
                disabled={bulkDeleting}
                testId="catalog-bulk-delete-confirm-btn"
                className="!bg-red-600 hover:!bg-red-700 !text-white !border-red-600"
              >
                {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Sep 20 2026 — Delete-by-File modal. Two-stage flow:
          Step 1: pick a file, click Preview → server returns match counts.
          Step 2: confirm to delete every matched catalog row. */}
      {showDeleteByFileModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={() => !deleteByFileBusy && (setShowDeleteByFileModal(false), setDeleteFile(null), setDeletePreview(null))}>
          <div className="bg-white rounded-sm border border-slate-300 w-full max-w-md"
               onClick={(e) => e.stopPropagation()}
               data-testid="catalog-delete-by-file-modal">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-sm flex items-center gap-2">
                <Trash2 size={14} className="text-rose-600" /> Delete Catalog by File
              </div>
              <button onClick={() => {
                if (deleteByFileBusy) return;
                setShowDeleteByFileModal(false);
                setDeleteFile(null);
                setDeletePreview(null);
              }} className="text-slate-500 hover:text-slate-800">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[11.5px] text-slate-600 leading-relaxed">
                Upload the same CSV / XLSX produced by <b>Export CSV</b>. Rows
                are matched by <code>(account_id, seller_sku)</code> — or
                <code>seller_sku</code> / <code>seller_asin</code> alone if
                account_id is omitted. Every matched catalog row will be
                permanently removed.
              </div>
              <Field label="Sheet (CSV / XLSX)">
                <input type="file"
                       accept=".csv,.txt,.xlsx"
                       onChange={(e) => {
                         setDeleteFile(e.target.files?.[0] || null);
                         setDeletePreview(null);
                       }}
                       data-testid="catalog-delete-by-file-input"
                       className="text-[12px] w-full border border-slate-300 rounded-sm px-2 py-1 bg-white" />
              </Field>
              {deletePreview && (
                <div className="bg-rose-50 border border-rose-200 rounded-sm px-3 py-2 space-y-1.5"
                     data-testid="catalog-delete-by-file-preview">
                  <div className="text-[12px] font-semibold text-rose-900">
                    {deletePreview.matched.toLocaleString()} catalog row{deletePreview.matched === 1 ? "" : "s"} will be deleted
                  </div>
                  <div className="text-[10.5px] font-mono text-rose-800/80">
                    Rows in file: {deletePreview.rows_parsed}
                    {" · "}Unmatched: {deletePreview.missing}
                  </div>
                  {Array.isArray(deletePreview.sample_missing) && deletePreview.sample_missing.length > 0 && (
                    <details className="text-[10.5px] font-mono">
                      <summary className="cursor-pointer text-rose-800 hover:text-rose-900">
                        Show unmatched preview ({deletePreview.sample_missing.length})
                      </summary>
                      <div className="mt-1 max-h-32 overflow-y-auto bg-white border border-rose-100 rounded-sm p-1.5 space-y-0.5">
                        {deletePreview.sample_missing.map((m, i) => (
                          <div key={i} className="text-slate-700">
                            row {m.row}: {m.seller_sku || m.seller_asin || "—"}
                            {m.account_id ? ` · ${m.account_id}` : ""}
                            {m.reason ? ` — ${m.reason}` : ""}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <Button onClick={() => {
                setShowDeleteByFileModal(false);
                setDeleteFile(null);
                setDeletePreview(null);
              }} disabled={deleteByFileBusy}
                      testId="catalog-delete-by-file-cancel">
                Cancel
              </Button>
              {!deletePreview ? (
                <Button onClick={previewDeleteByFile}
                        disabled={deleteByFileBusy || !deleteFile}
                        testId="catalog-delete-by-file-preview-btn"
                        className="!bg-slate-800 hover:!bg-slate-900 !text-white !border-slate-800 disabled:opacity-60">
                  {deleteByFileBusy ? "Scanning…" : "Preview Matches"}
                </Button>
              ) : (
                <Button onClick={confirmDeleteByFile}
                        disabled={deleteByFileBusy || !deletePreview.matched}
                        testId="catalog-delete-by-file-confirm-btn"
                        className="!bg-rose-600 hover:!bg-rose-700 !text-white !border-rose-600 disabled:opacity-60">
                  <Trash2 size={13} /> {deleteByFileBusy ? "Deleting…" : `Delete ${deletePreview.matched.toLocaleString()} rows`}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
